/*
 * Copyright (c) 2026. Bluemate B.V.
 */
import {getMode} from '@dvrd/dvr-controls';

/**
 * Which API answers the /dashboard sections: the real backend, or the Prism
 * mock generated from the OpenAPI document in nl-bolmate-core-mock/specs.
 *
 * The real backend is always the default; there is no build-time way to change
 * that. The mock is opt-in, per developer and per browser, and the choice is
 * read per request so switching needs no rebuild and no container restart:
 *
 *   ?overviewApi=mock | backend   — sticks for the current tab only
 *   window.bluemateApi.useMock()   — same thing from the console
 *
 * Only dev builds honour any of this, so a deployed app always talks to the
 * real backend.
 */
export type OverviewApiMode = 'backend' | 'mock';

/** Query parameter that switches the mode, and that OverviewApiUrlSync keeps in
 *  the address bar while the mock is selected. */
export const OVERVIEW_API_PARAM = 'overviewApi';
const MODE_STORAGE_KEY = 'bluemate.overviewApi';

/** Where the mock lives. The Vite dev server proxies this prefix to Prism. */
export const OVERVIEW_MOCK_BASE_URL = '/mock/';

function readStoredMode(): OverviewApiMode | null {
    try {
        const stored = window.sessionStorage.getItem(MODE_STORAGE_KEY);
        return stored === 'mock' || stored === 'backend' ? stored : null;
    } catch {
        // Private mode / storage disabled — fall back to the build-time default.
        return null;
    }
}

function storeMode(mode: OverviewApiMode): void {
    try {
        window.sessionStorage.setItem(MODE_STORAGE_KEY, mode);
    } catch {
        /* nothing to do — the mode simply will not stick */
    }
}

export function overviewApiMode(): OverviewApiMode {
    if (getMode() !== 'dev') return 'backend';

    const param = new URLSearchParams(window.location.search).get(OVERVIEW_API_PARAM);
    if (param === 'mock' || param === 'backend') {
        storeMode(param);
        return param;
    }
    return readStoredMode() ?? 'backend';
}

/** Base URL for the overview endpoints, or undefined to use the app-wide one.
 *  `sendFetch` prefers an explicit `baseUrl` over `window.settings.platformUrl`. */
export function overviewBaseUrl(): string | undefined {
    return overviewApiMode() === 'mock' ? OVERVIEW_MOCK_BASE_URL : undefined;
}

/** Console helper: `bluemateApi.useMock()` / `.useBackend()`. Reloads so the
 *  sections refetch against the newly selected API. */
export function installOverviewApiSwitch(): void {
    if (getMode() !== 'dev') return;
    // The mode used to persist in localStorage; drop the legacy key so an old
    // 'mock' choice cannot resurface as a surprise default at startup.
    try {
        window.localStorage.removeItem(MODE_STORAGE_KEY);
    } catch {
        /* nothing to do */
    }
    // Read the parameter now, at startup, rather than leaving it to whoever
    // calls overviewApiMode() first: views that canonicalise their own query
    // string (the invoice requests list rewrites it on mount) would otherwise
    // drop `?overviewApi=` before anything had a chance to store it.
    overviewApiMode();
    const apply = (mode: OverviewApiMode) => {
        storeMode(mode);
        console.warn(`[overview] switching to the ${mode} API — reloading`);
        window.location.reload();
    };
    (window as unknown as {bluemateApi: object}).bluemateApi = {
        mode: () => overviewApiMode(),
        useMock: () => apply('mock'),
        useBackend: () => apply('backend')
    };
}
